import { useState } from 'react';

import {
  verifyLotteryShareTweet,
  verifyNFTMintTweet,
  verifyAndClaimReward,
} from '@/api/pointsLottery';

export type VerificationType = 'lottery' | 'nft';

interface VerificationState {
  isVerifying: boolean;
  isVerified: boolean;
  tweetUrl: string | null;
  error: string | null;
}

/**
 * Hook for handling tweet verification flows
 * Supports both lottery reward claims and NFT minting
 */
export function useTweetVerification() {
  const [state, setState] = useState<VerificationState>({
    isVerifying: false,
    isVerified: false,
    tweetUrl: null,
    error: null,
  });

  /**
   * Open Twitter with pre-filled tweet text
   */
  const openTwitterShare = (tweetText: string) => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  /**
   * Verify tweet exists in user's timeline (checks Twitter API)
   */
  const verifyTweetExists = async (type: VerificationType) => {
    setState(prev => ({ ...prev, isVerifying: true, error: null }));

    try {
      const result = type === 'lottery'
        ? await verifyLotteryShareTweet()
        : await verifyNFTMintTweet();

      if (result.code === 0 && result.data?.verified) {
        setState({
          isVerifying: false,
          isVerified: true,
          tweetUrl: result.data.tweetUrl,
          error: null,
        });
        return { success: true, tweetUrl: result.data.tweetUrl };
      } else {
        const errorMessage = result.message || 'Tweet verification failed';
        setState({
          isVerifying: false,
          isVerified: false,
          tweetUrl: null,
          error: errorMessage,
        });
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Verification failed';
      setState({
        isVerifying: false,
        isVerified: false,
        tweetUrl: null,
        error: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Complete flow: verify tweet and enable action (claim or mint)
   */
  const verifyAndComplete = async (
    type: VerificationType,
    params: { winnerId?: number; seasonId?: number; tweetUrl?: string }
  ) => {
    setState(prev => ({ ...prev, isVerifying: true, error: null }));

    try {
      let finalTweetUrl: string;

      // If tweetUrl provided (manual paste), use it directly
      // Otherwise, verify tweet exists in user's timeline
      if (params.tweetUrl) {
        finalTweetUrl = params.tweetUrl;
      } else {
        const verifyResult = await verifyTweetExists(type);

        if (!verifyResult.success || !verifyResult.tweetUrl) {
          return { success: false, error: verifyResult.error };
        }

        finalTweetUrl = verifyResult.tweetUrl;
      }

      // Then, complete the action
      if (type === 'lottery' && params.winnerId) {
        const result = await verifyAndClaimReward({
          winnerId: params.winnerId,
          tweetUrl: finalTweetUrl,
        });

        setState({
          isVerifying: false,
          isVerified: true,
          tweetUrl: finalTweetUrl,
          error: null,
        });

        return { success: true, data: result };
      }

      throw new Error('Invalid verification type or missing parameters');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Verification failed';
      setState({
        isVerifying: false,
        isVerified: false,
        tweetUrl: null,
        error: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Reset verification state
   */
  const reset = () => {
    setState({
      isVerifying: false,
      isVerified: false,
      tweetUrl: null,
      error: null,
    });
  };

  return {
    ...state,
    openTwitterShare,
    verifyTweetExists,
    verifyAndComplete,
    reset,
  };
}
